import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CalendarDays, Images, MapPin, Pencil, Play, Plus, Trash2, User } from "lucide-react";
import { api, fmtDate, fmtErr, imgUrl, ytId } from "@/api";
import { useAuth } from "@/auth";
import { useLang } from "@/i18n";
import ImagePicker from "@/components/ImagePicker";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const emptyForm = { nama_kegiatan: "", pelaksana: "", tanggal: "", lokasi: "", photos: [], youtube_url: "" };

export default function GaleriPage() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("foto");
  const [galleries, setGalleries] = useState([]);
  const [videos, setVideos] = useState([]);
  const [dlg, setDlg] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [lightbox, setLightbox] = useState(null);
  const [player, setPlayer] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/galleries").then((r) => setGalleries(r.data)).catch(() => {});
    api.get("/videos").then((r) => setVideos(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const canEdit = (item) => user && (user.role === "admin" || user.id === item.uploader_id);

  const openCreate = (type) => {
    setForm(emptyForm);
    setDlg({ mode: "create", type });
  };
  const openEdit = (type, item) => {
    setForm({ nama_kegiatan: item.nama_kegiatan, pelaksana: item.pelaksana, tanggal: item.tanggal, lokasi: item.lokasi, photos: item.photos || [], youtube_url: item.youtube_url || "" });
    setDlg({ mode: "edit", type, item });
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let res;
      if (dlg.type === "foto") {
        const payload = { nama_kegiatan: form.nama_kegiatan, pelaksana: form.pelaksana, tanggal: form.tanggal, lokasi: form.lokasi, photos: form.photos };
        res = dlg.mode === "create" ? await api.post("/galleries", payload) : await api.put(`/galleries/${dlg.item.id}`, payload);
      } else {
        const payload = { nama_kegiatan: form.nama_kegiatan, pelaksana: form.pelaksana, tanggal: form.tanggal, lokasi: form.lokasi, youtube_url: form.youtube_url };
        res = dlg.mode === "create" ? await api.post("/videos", payload) : await api.put(`/videos/${dlg.item.id}`, payload);
      }
      toast.success(res?.data?.status === "pending" ? t("pending.notice") : t("common.saved"));
      setDlg(null);
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    }
    setBusy(false);
  };

  const remove = async (type, item) => {
    if (!window.confirm(t("common.confirm_delete"))) return;
    try {
      await api.delete(`/${type === "foto" ? "galleries" : "videos"}/${item.id}`);
      toast.success(t("common.deleted"));
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  const Field = ({ k, labelKey, type = "text" }) => (
    <div>
      <Label className="text-sm font-medium text-slate-700">{t(labelKey)}</Label>
      <Input type={type} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} required className="mt-1.5" data-testid={`gallery-form-${k}`} />
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20" data-testid="galeri-page">
      <h1 className="font-display text-4xl sm:text-5xl font-bold text-emerald-950 tracking-tight">{t("galeri.title")}</h1>
      <p className="text-slate-600 mt-3">{t("galeri.subtitle")}</p>

      <div className="flex flex-wrap items-center justify-between gap-4 mt-8">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="foto" data-testid="galeri-tab-foto"><Images className="h-4 w-4 mr-2" />{t("galeri.foto")}</TabsTrigger>
            <TabsTrigger value="video" data-testid="galeri-tab-video"><Play className="h-4 w-4 mr-2" />{t("galeri.video")}</TabsTrigger>
          </TabsList>
        </Tabs>
        {user ? (
          <Button onClick={() => openCreate(tab)} className="rounded-full bg-emerald-800 hover:bg-emerald-900" data-testid={tab === "foto" ? "upload-foto-button" : "upload-video-button"}>
            <Plus className="h-4 w-4 mr-2" /> {tab === "foto" ? t("galeri.upload_foto") : t("galeri.upload_video")}
          </Button>
        ) : (
          <Button variant="outline" onClick={() => navigate("/masuk")} className="rounded-full border-emerald-800 text-emerald-800" data-testid="galeri-login-cta">
            {t("auth.masuk")}
          </Button>
        )}
      </div>
      {!user && <p className="text-sm text-slate-500 mt-3" data-testid="galeri-login-note">{t("galeri.login_required")}</p>}

      {/* FOTO */}
      {tab === "foto" && (
        <div className="space-y-8 mt-10">
          {galleries.length === 0 && <p className="text-slate-500" data-testid="galeri-foto-empty">{t("galeri.empty")}</p>}
          {galleries.map((g) => (
            <div key={g.id} className="bg-white rounded-xl border border-emerald-900/10 shadow-sm p-6" data-testid={`gallery-card-${g.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                <div>
                  <h3 className="font-display text-2xl font-bold text-emerald-900">{g.nama_kegiatan}</h3>
                  {g.status === "pending" && (
                    <span className="inline-block mt-1 text-[11px] font-bold uppercase tracking-wide text-amber-700 bg-amber-100 border border-amber-300 rounded-full px-2.5 py-0.5" data-testid={`gallery-pending-${g.id}`}>{t("pending.badge")}</span>
                  )}
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs text-slate-500">
                    <span className="inline-flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" />{fmtDate(g.tanggal, lang)}</span>
                    <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{g.lokasi}</span>
                    <span className="inline-flex items-center gap-1"><User className="h-3.5 w-3.5" />{g.pelaksana}</span>
                  </div>
                </div>
                {canEdit(g) && (
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => openEdit("foto", g)} data-testid={`gallery-edit-${g.id}`}><Pencil className="h-3.5 w-3.5 mr-1" />{t("common.edit")}</Button>
                    <Button size="sm" variant="destructive" onClick={() => remove("foto", g)} data-testid={`gallery-delete-${g.id}`}><Trash2 className="h-3.5 w-3.5 mr-1" />{t("common.delete")}</Button>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-3 gap-3">
                {g.photos.slice(0, 3).map((p, i) => (
                  <button key={i} onClick={() => setLightbox(g)} className="relative aspect-video rounded-lg overflow-hidden group" data-testid={`gallery-thumb-${g.id}-${i}`}>
                    <img src={imgUrl(p)} alt="" className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    {i === 2 && g.photos.length > 3 && (
                      <span className="absolute inset-0 bg-black/55 text-white font-semibold flex items-center justify-center text-sm">+{g.photos.length - 3}</span>
                    )}
                  </button>
                ))}
              </div>
              <p className="text-xs text-slate-400 mt-3">{t("galeri.by")} {g.uploader_name}</p>
            </div>
          ))}
        </div>
      )}

      {/* VIDEO */}
      {tab === "video" && (
        <div className="space-y-8 mt-10">
          {videos.length === 0 && <p className="text-slate-500" data-testid="galeri-video-empty">{t("galeri.empty")}</p>}
          {videos.map((v) => {
            const yid = ytId(v.youtube_url);
            return (
              <div key={v.id} className="bg-white rounded-xl border border-emerald-900/10 shadow-sm p-6" data-testid={`video-card-${v.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                  <div>
                    <h3 className="font-display text-2xl font-bold text-emerald-900">{v.nama_kegiatan}</h3>
                    {v.status === "pending" && (
                      <span className="inline-block mt-1 text-[11px] font-bold uppercase tracking-wide text-amber-700 bg-amber-100 border border-amber-300 rounded-full px-2.5 py-0.5" data-testid={`video-pending-${v.id}`}>{t("pending.badge")}</span>
                    )}
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs text-slate-500">
                      <span className="inline-flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" />{fmtDate(v.tanggal, lang)}</span>
                      <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{v.lokasi}</span>
                      <span className="inline-flex items-center gap-1"><User className="h-3.5 w-3.5" />{v.pelaksana}</span>
                    </div>
                  </div>
                  {canEdit(v) && (
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => openEdit("video", v)} data-testid={`video-edit-${v.id}`}><Pencil className="h-3.5 w-3.5 mr-1" />{t("common.edit")}</Button>
                      <Button size="sm" variant="destructive" onClick={() => remove("video", v)} data-testid={`video-delete-${v.id}`}><Trash2 className="h-3.5 w-3.5 mr-1" />{t("common.delete")}</Button>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {[0, 1, 2].map((i) => (
                    <button key={i} onClick={() => i === 0 && yid && setPlayer(yid)} className={`relative aspect-video rounded-lg overflow-hidden group ${i > 0 ? "hidden md:block opacity-70 pointer-events-none" : ""}`} data-testid={`video-thumb-${v.id}-${i}`}>
                      {yid && <img src={`https://img.youtube.com/vi/${yid}/hqdefault.jpg`} alt="" className="h-full w-full object-cover" />}
                      {i === 0 && (
                        <span className="absolute inset-0 flex items-center justify-center">
                          <span className="h-14 w-14 rounded-full bg-black/60 group-hover:bg-[#D4AF37] flex items-center justify-center transition-colors">
                            <Play className="h-6 w-6 text-white group-hover:text-emerald-950 ml-1" />
                          </span>
                        </span>
                      )}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-slate-400 mt-3">{t("galeri.by")} {v.uploader_name}</p>
              </div>
            );
          })}
        </div>
      )}

      {/* Form dialog */}
      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-emerald-950">
              {dlg?.type === "foto" ? t("galeri.upload_foto") : t("galeri.upload_video")}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4 mt-2" data-testid="gallery-form">
            <Field k="nama_kegiatan" labelKey="galeri.nama_kegiatan" />
            <Field k="pelaksana" labelKey="galeri.pelaksana" />
            <div className="grid grid-cols-2 gap-4">
              <Field k="tanggal" labelKey="galeri.tanggal" type="date" />
              <Field k="lokasi" labelKey="galeri.lokasi" />
            </div>
            {dlg?.type === "foto" ? (
              <div>
                <Label className="text-sm font-medium text-slate-700">{t("galeri.lampirkan_foto")}</Label>
                <div className="mt-1.5">
                  <ImagePicker values={form.photos} onChange={(photos) => setForm({ ...form, photos })} max={9} testId="gallery-photo-picker" />
                </div>
              </div>
            ) : (
              <div>
                <Label className="text-sm font-medium text-slate-700">{t("galeri.lampirkan_video")}</Label>
                <Input value={form.youtube_url} onChange={(e) => setForm({ ...form, youtube_url: e.target.value })} required placeholder="https://www.youtube.com/watch?v=..." className="mt-1.5" data-testid="gallery-form-youtube" />
              </div>
            )}
            <div className="flex gap-3 pt-2">
              <Button type="submit" disabled={busy} className="flex-1 rounded-full bg-emerald-800 hover:bg-emerald-900" data-testid="gallery-form-submit">
                {busy ? t("common.loading") : t("common.save")}
              </Button>
              <Button type="button" variant="outline" onClick={() => setDlg(null)} className="rounded-full" data-testid="gallery-form-cancel">{t("common.cancel")}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Lightbox */}
      <Dialog open={!!lightbox} onOpenChange={(o) => !o && setLightbox(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-emerald-950">{lightbox?.nama_kegiatan}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2" data-testid="gallery-lightbox">
            {lightbox?.photos.map((p, i) => (
              <a key={i} href={imgUrl(p)} target="_blank" rel="noopener noreferrer">
                <img src={imgUrl(p)} alt="" className="aspect-video w-full object-cover rounded-lg hover:opacity-90 transition-opacity" />
              </a>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Video player */}
      <Dialog open={!!player} onOpenChange={(o) => !o && setPlayer(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="sr-only">Video</DialogTitle>
          </DialogHeader>
          {player && (
            <div className="aspect-video" data-testid="video-player">
              <iframe src={`https://www.youtube.com/embed/${player}?autoplay=1`} title="YouTube video" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="h-full w-full rounded-lg" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
